import { IFilterOptions } from '../components/Filter/Filter.types';
import { SortingInputData } from '../components/shared/SortingInput/SortingInput.types';
import { ActiveTablesSortProperty } from '../model/ActiveTablesSorting';
import { AvailableBotSearchFilterRequest } from '../model/availablebot/AvailableBotSearchFilterRequest';
import { SessionStorageKey } from '../model/enumeration/SessionStorageKey';
import { LobbyTableSearchFilter } from '../model/lobbytable/LobbyTableSearchFilter';
import { SortDirection } from '../model/SortDirection';

class SessionStorageStore {
  public addReadChatMessagesIds = (
    tableId: string,
    messageIds: string[],
  ): void =>
    sessionStorage.setItem(this.getChatKey(tableId), messageIds.join(','));

  public getReadChatMessagesIds = (tableId: string): string[] =>
    sessionStorage.getItem(this.getChatKey(tableId))?.split(',') || [];

  public removeReadChatMessagesIds = (tableId: string): void =>
    sessionStorage.removeItem(this.getChatKey(tableId));

  public addReadBotErrorIds = (tableId: string, errorIds: string[]): void => {
    const existingErrorIds = this.getReadBotErrorIds(tableId);
    const newErrorIds = [...existingErrorIds, ...errorIds];
    sessionStorage.setItem(this.getBotErrorKey(tableId), newErrorIds.join(','));
  };

  public getReadBotErrorIds = (tableId: string): string[] =>
    sessionStorage.getItem(this.getBotErrorKey(tableId))?.split(',') || [];

  public removeReadBotErrorIds = (tableId: string): void =>
    sessionStorage.removeItem(this.getBotErrorKey(tableId));

  public getSelectedFilterOptions = (): IFilterOptions | undefined => {
    const filterOptions = sessionStorage.getItem(
      SessionStorageKey.SELECTED_FILTER_OPTIONS,
    );
    return filterOptions ? JSON.parse(filterOptions) : undefined;
  };

  public setFilterOptions = (filterOptions: IFilterOptions): void => {
    sessionStorage.setItem(
      SessionStorageKey.SELECTED_FILTER_OPTIONS,
      JSON.stringify(filterOptions),
    );
  };

  public getSortingInputData = (key: string): SortingInputData | undefined => {
    const sortingInputData = sessionStorage.getItem(key);
    return sortingInputData ? JSON.parse(sortingInputData) : {};
  };

  public setSortingInputData = (
    key: string,
    sortingInputData: SortingInputData,
  ): void => {
    sessionStorage.setItem(key, JSON.stringify(sortingInputData));
  };

  public getActiveTablesSorting = (): Map<
    ActiveTablesSortProperty,
    SortDirection
  > => {
    const activeTablesSorting = sessionStorage.getItem(
      SessionStorageKey.ACTIVE_TABLES_SORTING,
    );
    return activeTablesSorting
      ? new Map(JSON.parse(activeTablesSorting))
      : new Map();
  };

  public setActiveTablesSorting = (
    activeTablesSorting: Map<ActiveTablesSortProperty, SortDirection>,
  ): void => {
    sessionStorage.setItem(
      SessionStorageKey.ACTIVE_TABLES_SORTING,
      JSON.stringify(activeTablesSorting),
    );
  };

  public getSearchValueExpertMode = (key: string): string => {
    const sortingInputData = sessionStorage.getItem(key);
    return sortingInputData ? JSON.parse(sortingInputData) : '';
  };

  public setSearchValueExpertMode = (key: string, value: string): void => {
    sessionStorage.setItem(key, JSON.stringify(value));
  };

  public setPinnedTables = (pinnedTableIds: string[]): void => {
    sessionStorage.setItem(
      SessionStorageKey.PINNED_TABLES,
      JSON.stringify(pinnedTableIds),
    );
  };

  public getPinnedTableIds = (): string[] => {
    const pinnedTableIds = sessionStorage.getItem(
      SessionStorageKey.PINNED_TABLES,
    );
    return pinnedTableIds ? JSON.parse(pinnedTableIds) : [];
  };

  public setTrackedBotIds = (botIds: string[]): void => {
    sessionStorage.setItem(
      SessionStorageKey.TRACKED_BOTS,
      JSON.stringify(botIds),
    );
  };

  public getTrackedBotIds = (): string[] => {
    const trackedBotIds = sessionStorage.getItem(
      SessionStorageKey.TRACKED_BOTS,
    );
    return trackedBotIds ? JSON.parse(trackedBotIds) : [];
  };

  public setLobbyTableSearchFilter = (
    searchFilter: LobbyTableSearchFilter,
  ): void => {
    sessionStorage.setItem(
      SessionStorageKey.LOBBY_TABLES_SEARCH_FILTER,
      JSON.stringify(searchFilter),
    );
  };

  public getLobbyTableSearchFilter = (): LobbyTableSearchFilter | undefined => {
    const lobbyTableSearchFilter = sessionStorage.getItem(
      SessionStorageKey.LOBBY_TABLES_SEARCH_FILTER,
    );
    return lobbyTableSearchFilter
      ? JSON.parse(lobbyTableSearchFilter)
      : undefined;
  };

  public setAvailableBotsSearchFilter = (
    searchFilter: AvailableBotSearchFilterRequest,
  ): void => {
    sessionStorage.setItem(
      SessionStorageKey.AVAILABLE_BOTS_SEARCH_FILTER,
      JSON.stringify(searchFilter),
    );
  };

  public getAvailableBotsSearchFilter = ():
    | AvailableBotSearchFilterRequest
    | undefined => {
    const availableBotsSearchFilter = sessionStorage.getItem(
      SessionStorageKey.AVAILABLE_BOTS_SEARCH_FILTER,
    );
    return availableBotsSearchFilter
      ? JSON.parse(availableBotsSearchFilter)
      : undefined;
  };

  private getChatKey = (tableId: string): string =>
    `${SessionStorageKey.CHAT}-${tableId}`;

  private getBotErrorKey = (tableId: string): string =>
    `${SessionStorageKey.BOT_ERROR}-${tableId}`;
}

const sessionStorageStore = new SessionStorageStore();
export default sessionStorageStore;
